import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Television, DownloadSimple, DeviceMobile, ArrowRight, CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOWNLOAD_URL = `${API}/download/apk`;

const STEPS = [
  'On your Firestick, install the free "Downloader" app from the Amazon Appstore.',
  "Open Settings → My Fire TV → Developer Options → Install unknown apps → enable Downloader.",
  "Open Downloader and type this address with your remote:",
  "Press Go, wait for the download, then hit Install — ReelVault boots straight into TV Mode.",
];

export default function TvInstall() {
  const [status, setStatus] = useState({ loading: true, available: false, size_mb: null });
  const dlRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/download/apk/status`)
      .then((r) => r.json())
      .then((d) => setStatus({ loading: false, ...d }))
      .catch(() => setStatus({ loading: false, available: false }));
  }, []);

  // Auto-start the download when a build is live (Downloader's browser triggers the install).
  useEffect(() => {
    if (status.available && dlRef.current) {
      const t = setTimeout(() => dlRef.current.click(), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [status.available]);

  const shortUrl = `${window.location.host}/apk`;

  return (
    <div className="min-h-screen bg-[#050505] text-[#EDEDED]">
      <Helmet>
        <title>Get ReelVault on Fire TV · Sideload</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* minimal top bar */}
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-4xl items-center gap-2 px-5 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="apk-home-link">
            <img src="/reelvault-mark.png" alt="ReelVault" className="h-7 w-auto" />
            <span className="font-serif text-xl tracking-tight">
              Reel<span className="text-crimson">Vault</span>
            </span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-14 rv-fade" data-testid="apk-landing">
        <span className="inline-flex items-center gap-2 border border-crimson/50 bg-crimson/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-crimson">
          <Television size={14} weight="duotone" /> Firestick & Android TV
        </span>
        <h1 className="mt-5 font-serif text-4xl sm:text-5xl tracking-tighter">
          ReelVault on your <span className="text-crimson">TV</span>
        </h1>
        <p className="mt-4 max-w-2xl text-white/60 text-sm leading-relaxed">
          Sideload the native app straight onto your Fire TV or Android TV — no Amazon Appstore needed.
          Channel-surf 24/7 live channels and thousands of free films from your couch with the remote.
        </p>

        {/* Download card */}
        <div className="mt-10 border border-white/10 bg-white/[0.03] p-6 sm:p-8">
          {status.loading ? (
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/30">Checking latest build…</p>
          ) : status.available ? (
            <>
              <a
                ref={dlRef}
                href={DOWNLOAD_URL}
                data-testid="apk-download-btn"
                className="inline-flex items-center gap-3 bg-amber px-7 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-crimson hover:text-white"
              >
                <DownloadSimple size={20} weight="bold" /> Download for Fire TV
                {status.size_mb ? <span className="opacity-70">· {status.size_mb} MB</span> : null}
              </a>
              <p className="mt-3 font-mono text-[11px] text-white/35">
                Your download should start automatically. If not, tap the button above.
              </p>
            </>
          ) : (
            <div data-testid="apk-unavailable">
              <p className="font-serif text-lg text-white/80">The TV build is on its way. 📺</p>
              <p className="mt-2 max-w-xl text-sm text-white/50">
                We're packaging the native Fire TV app right now. Bookmark this page — the direct download
                will appear here the moment it's live. Meanwhile, on phones & laptops you can{" "}
                <Link to="/" className="text-amber hover:text-crimson">use ReelVault in your browser</Link>{" "}
                or add it to your home screen.
              </p>
            </div>
          )}
        </div>

        {/* The Downloader paradigm */}
        <section className="mt-12">
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">
            Install with the “Downloader” app
          </h2>
          <div className="mt-4 flex items-center gap-3 border border-amber/40 bg-amber/5 px-5 py-4">
            <ArrowRight size={18} weight="bold" className="text-amber shrink-0" />
            <p className="text-sm text-white/80">
              In the Downloader app, just type{" "}
              <span data-testid="apk-short-url" className="font-mono text-amber">{shortUrl}</span>
            </p>
          </div>

          <ol className="mt-6 space-y-4">
            {STEPS.map((s, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/15 font-mono text-xs text-amber">
                  {i + 1}
                </span>
                <p className="pt-0.5 text-sm text-white/70">
                  {s}
                  {i === 2 && (
                    <span className="mt-1 block font-mono text-amber">{shortUrl}</span>
                  )}
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* Auto-updating note */}
        <section className="mt-12 grid gap-4 sm:grid-cols-2">
          <div className="border border-white/10 p-5">
            <CheckCircle size={22} weight="duotone" className="text-emerald-400" />
            <h3 className="mt-3 font-serif text-lg">Always up to date</h3>
            <p className="mt-1.5 text-sm text-white/55">
              The app loads ReelVault live, so new rows like Black Cinema and Live TV Guide show up
              instantly — no re-download needed.
            </p>
          </div>
          <div className="border border-white/10 p-5">
            <DeviceMobile size={22} weight="duotone" className="text-sky-400" />
            <h3 className="mt-3 font-serif text-lg">On a phone or laptop?</h3>
            <p className="mt-1.5 text-sm text-white/55">
              APK files are for TVs only. On mobile & desktop, just{" "}
              <Link to="/" className="text-amber hover:text-crimson">open ReelVault in your browser</Link>{" "}
              or add it to your home screen.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
